<template>
  <TransitionRoot appear :show="open" as="template">
    <Dialog as="div" @close="onClose">
      <div class="fixed inset-0 z-30 overflow-y-auto">
        <div class="min-h-screen px-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <DialogOverlay class="fixed inset-0 bg-black bg-opacity-60" />
          </TransitionChild>

          <span class="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span>

          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <div
              class="inline-block w-full max-w-sm overflow-hidden text-left align-middle transition-all transform bg-white rounded-2xl shadow-xl my-8"
            >
              <div class="bg-aces text-white px-4 py-3 flex items-center justify-between">
                <DialogTitle as="h3" class="font-semibold text-base">
                  {{ isPhone ? "Verificar teléfono" : "Verificar email" }}
                </DialogTitle>
                <button
                  type="button"
                  class="p-1 hover:bg-white hover:bg-opacity-20 rounded-full"
                  @click="onClose"
                >
                  <span class="sr-only">Cerrar</span>
                  <XIcon class="h-5 w-5" aria-hidden="true" />
                </button>
              </div>

              <div class="p-6">
                <div class="text-center mb-6">
                  <div
                    class="w-12 h-12 bg-aces bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-3"
                  >
                    <DeviceMobileIcon v-if="isPhone" class="w-6 h-6 text-aces" />
                    <MailIcon v-else class="w-6 h-6 text-aces" />
                  </div>
                  <p class="text-sm text-dark-gray">
                    {{ isPhone ? "Te enviamos un código por SMS al " : "Te enviamos un código a " }}
                    <span class="font-medium text-black">{{ destinationHint }}</span>
                  </p>
                </div>

                <div class="mb-4">
                  <label class="block text-sm text-dark-gray mb-3 text-center">Código</label>
                  <Transition
                    mode="out-in"
                    enter-active-class="transition-all duration-200 ease-out"
                    enter-from-class="opacity-0 scale-95"
                    enter-to-class="opacity-100 scale-100"
                    leave-active-class="transition-all duration-150 ease-in"
                    leave-from-class="opacity-100 scale-100"
                    leave-to-class="opacity-0 scale-95"
                  >
                    <div
                      v-if="otpBusy"
                      key="skeleton"
                      aria-live="polite"
                      aria-busy="true"
                    >
                      <div class="flex gap-2 justify-center">
                        <div
                          v-for="i in 6"
                          :key="i"
                          class="w-11 h-14 rounded-lg bg-light-gray animate-pulse"
                          :style="{ animationDelay: `${i * 80}ms` }"
                        />
                      </div>
                      <p class="text-sm text-dark-gray text-center mt-3 font-medium">
                        {{ otpBusyMessage }}
                      </p>
                    </div>
                    <div v-else key="otp">
                      <OtpInput
                        ref="otpInput"
                        v-model="code"
                        :length="6"
                        :invalid="wrongCode"
                        @complete="onComplete"
                      />
                      <p v-if="wrongCode" class="text-red-warning text-sm text-center mt-3">
                        El código no coincide con el enviado.
                      </p>
                    </div>
                  </Transition>
                </div>

                <p
                  v-if="!otpBusy"
                  class="text-sm text-dark-gray mt-4 text-center"
                >
                  <template v-if="resendSecondsLeft > 0">
                    Si no te llegó, podés pedir otro en
                    <span class="font-medium">{{ resendSecondsLeft }}s</span>.
                  </template>
                  <template v-else>
                    <button
                      type="button"
                      :disabled="sending"
                      class="text-aces hover:underline font-medium disabled:opacity-60"
                      @click="onResend"
                    >
                      {{ sending ? "Reenviando..." : "Reenviar código" }}
                    </button>
                  </template>
                </p>

                <p
                  v-if="!isPhone"
                  class="text-xs text-dark-gray mt-2 text-center"
                >
                  Revisá la carpeta de spam si no lo encontrás en tu bandeja de entrada.
                </p>

                <button
                  type="button"
                  :disabled="otpBusy || code.length !== 6"
                  class="btn-primary mt-6 lg:text-lg"
                  @click="onConfirm"
                >
                  {{ verifying ? "Verificando..." : "Confirmar" }}
                </button>

                <button
                  type="button"
                  class="w-full text-aces font-medium py-2 mt-2 hover:underline"
                  @click="onClose"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
  import axios from "axios";
  import {
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogOverlay,
    DialogTitle,
  } from "@headlessui/vue";
  import { XIcon, DeviceMobileIcon, MailIcon } from "@heroicons/vue/solid";
  import { createToast } from "mosha-vue-toastify";
  import { setSubscriberToken } from "../../auth";
  import OtpInput from "../OtpInput.vue";

  export default {
    name: "VerifyOtpModal",
    components: {
      TransitionRoot,
      TransitionChild,
      Dialog,
      DialogOverlay,
      DialogTitle,
      XIcon,
      DeviceMobileIcon,
      MailIcon,
      OtpInput,
    },
    props: {
      open: { type: Boolean, default: false },
      /** "phone" | "email" */
      type: { type: String, default: "phone" },
      clientId: { type: [Number, String], default: null },
      docType: { type: String, default: "DNI" },
      documentNumber: { type: String, default: "" },
      maskedDestination: { type: String, default: "" },
      destinationFallback: { type: String, default: "" },
    },
    emits: ["update:open", "verified", "close"],
    data() {
      return {
        code: "",
        wrongCode: false,
        sending: false,
        verifying: false,
        resendSecondsLeft: 0,
        resendTimerId: null,
        currentMaskedDestination: this.maskedDestination || "",
      };
    },
    computed: {
      isPhone() {
        return this.type === "phone";
      },
      destinationHint() {
        if (this.currentMaskedDestination) return this.currentMaskedDestination;
        if (this.destinationFallback) return this.destinationFallback;
        return this.isPhone ? "tu teléfono" : "tu correo";
      },
      otpBusy() {
        return this.verifying || this.sending;
      },
      otpBusyMessage() {
        if (this.verifying) return "Validando...";
        return "Enviando código...";
      },
    },
    watch: {
      open(val) {
        if (val) {
          this.resetState();
        }
      },
      maskedDestination(val) {
        if (val) this.currentMaskedDestination = val;
      },
    },
    beforeUnmount() {
      this.clearResendCooldown();
    },
    methods: {
      resetState() {
        this.code = "";
        this.wrongCode = false;
        this.startResendCooldown();
      },
      startResendCooldown(seconds = 30) {
        this.clearResendCooldown();
        this.resendSecondsLeft = seconds;
        this.resendTimerId = setInterval(() => {
          this.resendSecondsLeft -= 1;
          if (this.resendSecondsLeft <= 0) {
            this.clearResendCooldown();
          }
        }, 1000);
      },
      clearResendCooldown() {
        if (this.resendTimerId) clearInterval(this.resendTimerId);
        this.resendTimerId = null;
        this.resendSecondsLeft = 0;
      },
      onClose() {
        if (this.verifying || this.sending) return;
        this.clearResendCooldown();
        this.$emit("close");
        this.$emit("update:open", false);
      },
      onComplete() {
        if (!this.verifying) this.onConfirm();
      },
      async onResend() {
        if (this.resendSecondsLeft > 0 || this.sending) return;
        if (!this.clientId) return;
        this.sending = true;
        try {
          const endpoint = this.isPhone
            ? "subscriber-auth/phone/send-verification-code"
            : "subscriber-auth/email/send-verification-code";
          const { data } = await axios.post(endpoint, {
            clientId: this.clientId,
            docType: this.docType,
            documentNumber: String(this.documentNumber || "").trim(),
          });
          if (data.success) {
            if (this.isPhone && data.maskedPhone) {
              this.currentMaskedDestination = data.maskedPhone;
            } else if (!this.isPhone && data.maskedEmail) {
              this.currentMaskedDestination = data.maskedEmail;
            }
            this.code = "";
            this.wrongCode = false;
            this.startResendCooldown();
            createToast("Código reenviado.", {
              position: "top-center",
              type: "success",
              transition: "zoom",
            });
          } else {
            createToast(data.errorMessage || "No se pudo reenviar el código.", {
              position: "top-center",
              type: "danger",
              transition: "zoom",
            });
          }
        } catch (err) {
          const body = err.response?.data;
          const msg =
            (typeof body === "string" && body) ||
            body?.errorMessage ||
            body?.title ||
            "No se pudo reenviar el código.";
          createToast(msg, {
            position: "top-center",
            type: "danger",
            transition: "zoom",
          });
        } finally {
          this.sending = false;
        }
      },
      async onConfirm() {
        const trimmed = String(this.code || "").trim();
        if (!this.clientId) return;
        if (trimmed.length !== 6) return;
        this.verifying = true;
        try {
          const endpoint = this.isPhone
            ? "subscriber-auth/phone/verify-code"
            : "subscriber-auth/email/verify-code";
          const { data } = await axios.post(endpoint, {
            clientId: this.clientId,
            docType: this.docType,
            documentNumber: String(this.documentNumber || "").trim(),
            code: trimmed,
          });
          if (data.success) {
            this.wrongCode = false;
            this.code = "";
            this.clearResendCooldown();
            if (data.token) {
              await setSubscriberToken(data.token);
            }
            this.$emit("verified", { type: this.type });
            this.$emit("update:open", false);
            return;
          }
          this.wrongCode = true;
          if (data.errorMessage) {
            createToast(data.errorMessage, {
              position: "top-center",
              type: "danger",
              transition: "zoom",
            });
          }
        } catch (err) {
          this.wrongCode = true;
          const body = err.response?.data;
          const msg =
            body?.errorMessage ||
            body?.title ||
            "Código incorrecto o expirado.";
          createToast(msg, {
            position: "top-center",
            type: "danger",
            transition: "zoom",
          });
        } finally {
          this.verifying = false;
        }
      },
    },
  };
</script>
